import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function Profile() {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const [profileData, setProfileData] = useState({
    username: user.username,
    email: user.useremail,
  });

  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false
  });

  const [withdrawalPinData, setWithdrawalPinData] = useState({
    currentPin: '',
    newPin: '',
    confirmPin: ''
  });

  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    promotionalEmails: true,
    language: 'en',
    currency: 'USD'
  });

  // Referral Code State
  const [referralCode, setReferralCode] = useState('');
  const [savedReferralCode, setSavedReferralCode] = useState('PLAYER123REF');
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const [referredFriends, setReferredFriends] = useState([
    {
      id: 1,
      username: 'Friend001',
      joinDate: '2024-01-15',
      firstDeposit: 100,
      status: 'Completed',
      reward: 10
    },
    {
      id: 2,
      username: 'Friend002',
      joinDate: '2024-01-20',
      firstDeposit: 50,
      status: 'Completed',
      reward: 10
    },
    {
      id: 3,
      username: 'Friend003',
      joinDate: '2024-01-25',
      firstDeposit: 0,
      status: 'Pending',
      reward: 0
    }
  ]);

  const handleCheckReferralCode = () => {
    if (!referralCode.trim()) {
      alert('Please enter a referral code');
      return;
    }

    setIsCheckingCode(true);

    // Simulate API call
    setTimeout(() => {
      // Mock duplicate check - randomly determine availability
      const isAvailable = Math.random() > 0.3;
      setCodeAvailable(isAvailable);
      setIsCheckingCode(false);
    }, 1000);
  };

  const handleSaveReferralCode = () => {
    if (!referralCode.trim()) {
      alert('Please enter a referral code');
      return;
    }

    if (codeAvailable === false) {
      alert('This code is already taken. Please choose another one.');
      return;
    }

    if (codeAvailable === null) {
      alert('Please check code availability first');
      return;
    }

    setSavedReferralCode(referralCode);
    alert('Referral code saved successfully!');
    setReferralCode('');
    setCodeAvailable(null);
  };

  const copyReferralLink = () => {
    const referralLink = `https://spinverse-slot.com/signup?ref=${savedReferralCode}`;
    navigator.clipboard.writeText(referralLink);
    alert('Referral link copied to clipboard!');
  };

  const handleProfileUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Profile updated:', profileData);
    alert('Profile updated successfully!');
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (securityData.newPassword !== securityData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }
    console.log('Password changed');
    alert('Password changed successfully!');
  };

  const handleWithdrawalPinChange = (e: React.FormEvent) => {
    e.preventDefault();

    if (withdrawalPinData.currentPin.length !== 6) {
      alert('Current PIN must be 6 digits!');
      return;
    }

    if (withdrawalPinData.newPin.length !== 6) {
      alert('New PIN must be 6 digits!');
      return;
    }

    if (withdrawalPinData.newPin !== withdrawalPinData.confirmPin) {
      alert('New PINs do not match!');
      return;
    }

    console.log('Withdrawal PIN changed');
    alert('Withdrawal PIN changed successfully!');
    setWithdrawalPinData({
      currentPin: '',
      newPin: '',
      confirmPin: ''
    });
  };

  const handlePreferencesUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Preferences updated:', preferences);
    alert('Preferences updated successfully!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D0E11] via-[#111319] to-[#16181D]">
      {/* Header */}
      <header className="bg-[#1a1c24] border-b border-[#FFD349]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Mobile Balance */}
            <div className="flex flex-col gap-2">
              <Link to="/" className="flex items-center gap-3">
                <h1 className="flex items-center gap-2">
                  <div className="relative w-10 h-10 flex items-center justify-center">
                    <img
                      src="https://public.readdy.ai/ai/img_res/aa29cde3-a1c3-46be-b8a4-d1063c9a3299.jpg"
                      alt="DIGITWISE Logo"
                      className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(239,191,101,0.4)]"
                    />
                  </div>
                  <span className="text-3xl font-bold bg-gradient-to-r from-[#FFD349] via-[#FFC107] to-[#FF9800] bg-clip-text text-transparent" style={{
                    fontFamily: 'Orbitron, sans-serif'
                  }}>
                    DIGITWISE
                  </span>
                </h1>
              </Link>

              {/* Mobile Balance - Below Logo */}
              {user && (
                <div className="md:hidden flex items-center justify-center gap-1 bg-[#0D0E11] border border-[#FFD349]/20 rounded px-3 py-1.5 mx-auto w-fit">
                  <i className="ri-wallet-3-line text-[#FFD349] text-sm"></i>
                  <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    ${user.balance.toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Home
              </Link>
              <Link to="/dashboard" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Dashboard
              </Link>
              <Link to="/wallet" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Wallet
              </Link>
              <Link to="/profile" className="text-[#FFD349] text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Profile
              </Link>
            </nav>

            {/* Desktop Balance & Logout */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-3 bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-2">
                <i className="ri-wallet-3-line text-[#FFD349] text-lg"></i>
                <div>
                  <p className="text-white/60 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>Balance</p>
                  <p className="text-[#FFD349] font-bold text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    ${user?.balance.toLocaleString() || '0'}
                  </p>
                </div>
              </div>
              <button
                onClick={logout}
                className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black px-6 py-2 rounded-lg font-bold text-sm hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] transition-all whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Logout
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2 hover:text-[#FFD349] transition-colors cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t border-[#FFD349]/20">
              <nav className="flex flex-col gap-3">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-home-line mr-2"></i>
                  Home
                </Link>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-dashboard-line mr-2"></i>
                  Dashboard
                </Link>
                <Link
                  to="/wallet"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-wallet-3-line mr-2"></i>
                  Wallet
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#FFD349] text-sm font-medium py-2 px-4 rounded-lg bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-user-line mr-2"></i>
                  Profile
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] text-left cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-logout-box-line mr-2"></i>
                  Logout
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            My Profile
          </h2>
          <p className="text-white/60 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Manage your account settings and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Mobile Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
              {/* Avatar */}
              <div className="text-center mb-4 md:mb-6">
                <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-gradient-to-r from-[#FFD349] to-[#FFC107] rounded-full mx-auto mb-3 md:mb-4">
                  <span className="text-black text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    {profileData.username.charAt(0)}
                  </span>
                </div>
                <h3 className="text-white font-bold text-base md:text-lg mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {profileData.username}
                </h3>
                <p className="text-white/60 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  VIP Level 3
                </p>
              </div>

              {/* Menu */}
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer ${activeTab === 'profile'
                      ? 'bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black'
                      : 'text-white/70 hover:text-white hover:bg-[#0D0E11]'
                    }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-user-line text-base md:text-lg"></i>
                  Profile Info
                </button>
                <button
                  onClick={() => setActiveTab('referral')}
                  className={`w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer ${activeTab === 'referral'
                      ? 'bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black'
                      : 'text-white/70 hover:text-white hover:bg-[#0D0E11]'
                    }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-user-add-line text-base md:text-lg"></i>
                  My Referral Code
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer ${activeTab === 'security'
                      ? 'bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black'
                      : 'text-white/70 hover:text-white hover:bg-[#0D0E11]'
                    }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-shield-line text-base md:text-lg"></i>
                  Security
                </button>
                <button
                  onClick={() => setActiveTab('preferences')}
                  className={`w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer ${activeTab === 'preferences'
                      ? 'bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black'
                      : 'text-white/70 hover:text-white hover:bg-[#0D0E11]'
                    }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-settings-line text-base md:text-lg"></i>
                  Preferences
                </button>
              </nav>

              <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-[#FFD349]/20">
                <button className="w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm font-semibold text-red-400 hover:bg-red-400/10 transition-all cursor-pointer" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <i className="ri-logout-box-line text-base md:text-lg"></i>
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {/* Profile Info Tab */}
            {activeTab === 'profile' && (
              <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  Profile Information
                </h3>

                <form onSubmit={handleProfileUpdate} className="space-y-4 md:space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Username
                      </label>
                      <input
                        type="text"
                        value={profileData.username}
                        onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>

                    {/* <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Wallet Address (TRC-20)
                      </label>
                      <input
                        type="text"
                        value={profileData.walletAddress}
                        onChange={(e) => setProfileData({...profileData, walletAddress: e.target.value})}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div> */}
                  </div>

                  <button
                    type="submit"
                    className="w-full md:w-auto bg-gradient-to-r from-[#FFD349] to-[#FFC107] hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] text-black font-bold px-6 md:px-8 py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Save Changes
                  </button>
                </form>
              </div>
            )}

            {/* Referral Code Tab */}
            {activeTab === 'referral' && (
              <div className="space-y-6">
                {/* Referral Code Creation */}
                <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    My Referral Code
                  </h3>

                  {/* Current Referral Code */}
                  <div className="mb-6 p-4 bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg">
                    <p className="text-white/60 text-sm mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Your Current Referral Code
                    </p>
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-[#FFD349] text-xl md:text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        {savedReferralCode}
                      </p>
                      <button
                        onClick={copyReferralLink}
                        className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black px-4 py-2 rounded-lg font-semibold text-sm hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] transition-all whitespace-nowrap cursor-pointer"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        <i className="ri-file-copy-line mr-2"></i>
                        Copy Link
                      </button>
                    </div>
                  </div>

                  {/* Create New Referral Code */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Create Custom Referral Code
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={referralCode}
                          onChange={(e) => {
                            setReferralCode(e.target.value.toUpperCase());
                            setCodeAvailable(null);
                          }}
                          placeholder="Enter your custom code"
                          className="flex-1 bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          maxLength={20}
                        />
                        <button
                          onClick={handleCheckReferralCode}
                          disabled={isCheckingCode || !referralCode.trim()}
                          className="bg-[#0D0E11] border border-[#FFD349]/30 text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#FFD349]/10 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          {isCheckingCode ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              Checking...
                            </>
                          ) : (
                            <>
                              <i className="ri-search-line mr-2"></i>
                              Check
                            </>
                          )}
                        </button>
                      </div>

                      {/* Availability Status */}
                      {codeAvailable !== null && (
                        <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${codeAvailable
                            ? 'bg-green-500/10 border border-green-500/30'
                            : 'bg-red-500/10 border border-red-500/30'
                          }`}>
                          <i className={`${codeAvailable ? 'ri-checkbox-circle-line text-green-400' : 'ri-close-circle-line text-red-400'
                            } text-lg`}></i>
                          <p className={`text-sm font-medium ${codeAvailable ? 'text-green-400' : 'text-red-400'
                            }`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {codeAvailable
                              ? '✓ This code is available!'
                              : '✗ This code is already taken. Please try another one.'}
                          </p>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleSaveReferralCode}
                      disabled={!codeAvailable}
                      className="w-full md:w-auto bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black font-bold px-8 py-3 rounded-lg hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <i className="ri-save-line mr-2"></i>
                      Save Referral Code
                    </button>
                  </div>
                </div>

                {/* Referred Friends List */}
                <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <h3 className="text-lg md:text-xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                      Referred Friends
                    </h3>
                    <div className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black px-4 py-2 rounded-lg font-bold text-sm">
                      {referredFriends.length} / 10
                    </div>
                  </div>

                  {/* Stats Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg p-4">
                      <p className="text-white/60 text-xs mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Total Referred
                      </p>
                      <p className="text-white text-xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        {referredFriends.length}
                      </p>
                    </div>
                    <div className="bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg p-4">
                      <p className="text-white/60 text-xs mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Completed
                      </p>
                      <p className="text-green-400 text-xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        {referredFriends.filter(f => f.status === 'Completed').length}
                      </p>
                    </div>
                    <div className="bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg p-4 col-span-2 md:col-span-1">
                      <p className="text-white/60 text-xs mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Total Rewards
                      </p>
                      <p className="text-[#FFD349] text-xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        ${referredFriends.reduce((sum, f) => sum + f.reward, 0)}
                      </p>
                    </div>
                  </div>

                  {/* Friends Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#FFD349]/20">
                          <th className="text-left text-white/60 text-xs font-semibold py-3 px-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Username
                          </th>
                          <th className="text-left text-white/60 text-xs font-semibold py-3 px-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Join Date
                          </th>
                          <th className="text-left text-white/60 text-xs font-semibold py-3 px-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            First Deposit
                          </th>
                          <th className="text-left text-white/60 text-xs font-semibold py-3 px-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Status
                          </th>
                          <th className="text-left text-white/60 text-xs font-semibold py-3 px-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Reward
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {referredFriends.map((friend) => (
                          <tr key={friend.id} className="border-b border-[#FFD349]/10 hover:bg-[#0D0E11]/50 transition-colors">
                            <td className="py-4 px-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-[#FFD349] to-[#FFC107] rounded-full">
                                  <span className="text-black text-xs font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                    {friend.username.charAt(0)}
                                  </span>
                                </div>
                                <span className="text-white text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                  {friend.username}
                                </span>
                              </div>
                            </td>
                            <td className="py-4 px-2 text-white/70 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                              {friend.joinDate}
                            </td>
                            <td className="py-4 px-2 text-white text-sm font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                              {friend.firstDeposit > 0 ? `$${friend.firstDeposit}` : '-'}
                            </td>
                            <td className="py-4 px-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${friend.status === 'Completed'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                                }`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                {friend.status}
                              </span>
                            </td>
                            <td className="py-4 px-2 text-[#FFD349] text-sm font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                              {friend.reward > 0 ? `+$${friend.reward}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {referredFriends.length === 0 && (
                    <div className="text-center py-12">
                      <i className="ri-user-add-line text-white/20 text-5xl mb-4"></i>
                      <p className="text-white/60 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        No referred friends yet. Start sharing your referral code!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    Change Password
                  </h3>

                  <form onSubmit={handlePasswordChange} className="space-y-4 md:space-y-5">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={securityData.currentPassword}
                        onChange={(e) => setSecurityData({ ...securityData, currentPassword: e.target.value })}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        New Password
                      </label>
                      <input
                        type="password"
                        value={securityData.newPassword}
                        onChange={(e) => setSecurityData({ ...securityData, newPassword: e.target.value })}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={securityData.confirmPassword}
                        onChange={(e) => setSecurityData({ ...securityData, confirmPassword: e.target.value })}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full md:w-auto bg-gradient-to-r from-[#FFD349] to-[#FFC107] hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] text-black font-bold px-6 md:px-8 py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Update Password
                    </button>
                  </form>
                </div>

                <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    Change Withdrawal PIN
                  </h3>

                  <form onSubmit={handleWithdrawalPinChange} className="space-y-4 md:space-y-5">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Current Withdrawal PIN
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPin ? 'text' : 'password'}
                          value={withdrawalPinData.currentPin}
                          onChange={(e) => setWithdrawalPinData({ ...withdrawalPinData, currentPin: e.target.value })}
                          className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors pr-12"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          placeholder="Enter current 6-digit PIN"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPin(!showCurrentPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors cursor-pointer"
                        >
                          <i className={`ri-eye-${showCurrentPin ? 'off' : ''}-line text-lg`}></i>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        New Withdrawal PIN
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPin ? 'text' : 'password'}
                          value={withdrawalPinData.newPin}
                          onChange={(e) => setWithdrawalPinData({ ...withdrawalPinData, newPin: e.target.value })}
                          className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors pr-12"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          placeholder="Enter new 6-digit PIN"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPin(!showNewPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors cursor-pointer"
                        >
                          <i className={`ri-eye-${showNewPin ? 'off' : ''}-line text-lg`}></i>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Confirm New Withdrawal PIN
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPin ? 'text' : 'password'}
                          value={withdrawalPinData.confirmPin}
                          onChange={(e) => setWithdrawalPinData({ ...withdrawalPinData, confirmPin: e.target.value })}
                          className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors pr-12"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          placeholder="Re-enter new 6-digit PIN"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPin(!showConfirmPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors cursor-pointer"
                        >
                          <i className={`ri-eye-${showConfirmPin ? 'off' : ''}-line text-lg`}></i>
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full md:w-auto bg-gradient-to-r from-[#FFD349] to-[#FFC107] hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] text-black font-bold px-6 md:px-8 py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Update Withdrawal PIN
                    </button>
                  </form>
                </div>

                <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    Two-Factor Authentication
                  </h3>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold mb-1 text-sm md:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Enable 2FA
                      </p>
                      <p className="text-white/60 text-xs md:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Add an extra layer of security to your account
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={securityData.twoFactorEnabled}
                        onChange={(e) => setSecurityData({ ...securityData, twoFactorEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-[#0D0E11] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[#FFD349] peer-checked:to-[#FFC107]"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  Preferences
                </h3>

                <form onSubmit={handlePreferencesUpdate} className="space-y-6">
                  {/* Notifications */}
                  <div>
                    <h4 className="text-white font-semibold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Notifications
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Email Notifications</p>
                          <p className="text-white/50 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>Receive updates via email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.emailNotifications}
                            onChange={(e) => setPreferences({ ...preferences, emailNotifications: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-14 h-7 bg-[#0D0E11] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[#FFD349] peer-checked:to-[#FFC107]"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>SMS Notifications</p>
                          <p className="text-white/50 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>Receive updates via SMS</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.smsNotifications}
                            onChange={(e) => setPreferences({ ...preferences, smsNotifications: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-14 h-7 bg-[#0D0E11] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[#FFD349] peer-checked:to-[#FFC107]"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Promotional Emails</p>
                          <p className="text-white/50 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>Receive special offers and promotions</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.promotionalEmails}
                            onChange={(e) => setPreferences({ ...preferences, promotionalEmails: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-14 h-7 bg-[#0D0E11] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[#FFD349] peer-checked:to-[#FFC107]"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Language & Currency */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Language
                      </label>
                      <select
                        value={preferences.language}
                        onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
                        className="w-full bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors cursor-pointer"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        <option value="en">English</option>
                        <option value="ko">한국어</option>
                        <option value="ja">日本語</option>
                        <option value="zh">中文</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Preferred Currency
                      </label>
                      <select
                        value={preferences.currency}
                        onChange={(e) => setPreferences({ ...preferences, currency: e.target.value })}
                        className="w-full px-4 py-3 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#FFD349] text-sm"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        <option value="USDT" className="bg-[#0D0E11]">USDT</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full md:w-auto bg-gradient-to-r from-[#FFD349] to-[#FFC107] hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] text-black font-bold px-6 md:px-8 py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Save Preferences
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
